import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Alert, AlertDescription } from '../../components/ui/alert';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import { toast } from 'sonner';
import api from '../../utils/api';
import { 
  ArrowLeft, Users, Clock, MapPin, CheckCircle2, XCircle, 
  AlertTriangle, RefreshCw, MoreVertical, User, LogIn, LogOut,
  QrCode, Camera, Shield
} from 'lucide-react';
import { formatTime12h, formatTimeRange, formatDateTime12h, formatDateMMDDYYYY } from '../../utils/timeFormat';

const ShiftLeadDashboard = () => {
  const { shiftId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [roster, setRoster] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [actionLoading, setActionLoading] = useState(null);

  const fetchRoster = useCallback(async () => {
    try {
      const response = await api.get(`/shift-lead/roster/${shiftId}`);
      setRoster(response.data);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('You are not authorized as shift lead for this event');
        navigate(-1);
      } else {
        toast.error('Failed to load roster');
      }
      console.error(error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [shiftId, navigate]);

  useEffect(() => {
    fetchRoster();
    // Auto-refresh every 15 seconds
    const interval = setInterval(fetchRoster, 15000);
    return () => clearInterval(interval);
  }, [fetchRoster]);

  const handleAction = async (bookingId, action, talentName) => {
    setActionLoading(bookingId);
    try {
      await api.post('/shift-lead/manual-action', {
        booking_id: bookingId,
        action: action,
        override_gps: true
      });
      toast.success(`${talentName} marked as ${action.replace('_', ' ')}`);
      await fetchRoster();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Action failed');
    } finally {
      setActionLoading(null);
    }
  };

  const handleRefresh = () => {
    setRefreshing(true);
    fetchRoster();
  };

  const getStatusBadge = (status) => {
    const config = {
      checked_in: { label: 'Checked In', className: 'bg-green-500', icon: CheckCircle2 },
      checked_out: { label: 'Checked Out', className: 'bg-blue-500', icon: CheckCircle2 },
      pending: { label: 'Pending', className: 'bg-slate-400', icon: Clock },
      no_show: { label: 'No Show', className: 'bg-red-500', icon: XCircle },
    };
    const c = config[status] || config.pending;
    const Icon = c.icon;
    return (
      <Badge className={`${c.className} text-white`}>
        <Icon className="w-3 h-3 mr-1" />
        {c.label}
      </Badge>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-600"></div>
      </div>
    );
  }

  if (!roster) {
    return (
      <div className="min-h-screen bg-slate-50 p-6">
        <div className="max-w-lg mx-auto text-center py-12">
          <Shield className="w-12 h-12 mx-auto text-slate-300 mb-4" />
          <h2 className="text-xl font-semibold text-slate-700">Access Denied</h2>
          <p className="text-slate-500 mt-2">You must be assigned as shift lead to access this dashboard.</p>
          <Button variant="outline" className="mt-4" onClick={() => navigate(-1)}>
            <ArrowLeft className="w-4 h-4 mr-2" /> Go Back
          </Button>
        </div>
      </div>
    );
  }

  const { shift, staff, summary } = roster;

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-gradient-to-r from-cyan-600 to-teal-600 text-white sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="icon" onClick={() => navigate(-1)} className="text-white hover:bg-white/20">
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div>
                <div className="flex items-center gap-2">
                  <Shield className="w-5 h-5" />
                  <h1 className="text-lg font-bold">Shift Lead Dashboard</h1>
                </div>
                <p className="text-cyan-100 text-sm">{shift.venue_name}</p>
              </div>
            </div>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={handleRefresh}
              disabled={refreshing}
              className="text-white hover:bg-white/20"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-6 space-y-4">
        {/* Event Info */}
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div className="space-y-1">
                <div className="flex items-center gap-2 text-sm text-slate-500">
                  <Clock className="w-4 h-4" />
                  {formatDateMMDDYYYY(shift.shift_date)} • {formatTimeRange(shift.start_time, shift.end_time)}
                </div>
                <div className="flex items-center gap-2 text-sm text-slate-500">
                  <MapPin className="w-4 h-4" />
                  {shift.role_name}
                </div>
              </div>
              <Link to={`/talent/checkin-scanner/${shiftId}`}>
                <Button variant="outline" size="sm">
                  <QrCode className="w-4 h-4 mr-2" />
                  QR Scanner
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>

        {/* Stats */}
        <div className="grid grid-cols-4 gap-3">
          <Card className="bg-slate-100">
            <CardContent className="pt-4 text-center">
              <p className="text-2xl font-bold text-slate-700">{summary.total}</p>
              <p className="text-xs text-slate-500">Total</p>
            </CardContent>
          </Card>
          <Card className="bg-green-50">
            <CardContent className="pt-4 text-center">
              <p className="text-2xl font-bold text-green-600">{summary.checked_in}</p>
              <p className="text-xs text-green-700">In</p>
            </CardContent>
          </Card>
          <Card className="bg-blue-50">
            <CardContent className="pt-4 text-center">
              <p className="text-2xl font-bold text-blue-600">{summary.checked_out}</p>
              <p className="text-xs text-blue-700">Out</p>
            </CardContent>
          </Card>
          <Card className="bg-red-50">
            <CardContent className="pt-4 text-center">
              <p className="text-2xl font-bold text-red-600">{summary.no_shows}</p>
              <p className="text-xs text-red-700">No Show</p>
            </CardContent>
          </Card>
        </div>

        {/* Staff Roster */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Users className="w-5 h-5" />
              Staff Roster
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {staff.map((member) => (
              <div 
                key={member.booking_id}
                className={`flex items-center gap-3 p-3 rounded-lg border transition-colors ${
                  member.attendance_status === 'checked_in' ? 'bg-green-50 border-green-200' :
                  member.attendance_status === 'checked_out' ? 'bg-blue-50 border-blue-200' :
                  member.attendance_status === 'no_show' ? 'bg-red-50 border-red-200' :
                  'bg-white border-slate-200'
                }`}
                data-testid={`staff-${member.user_id}`}
              >
                {/* Profile Photo */}
                <div className="flex-shrink-0">
                  {member.profile_photo_url ? (
                    <img 
                      src={member.profile_photo_url} 
                      alt={member.first_name}
                      className="w-12 h-12 rounded-full object-cover"
                    />
                  ) : (
                    <div className="w-12 h-12 rounded-full bg-slate-200 flex items-center justify-center">
                      <User className="w-5 h-5 text-slate-400" />
                    </div>
                  )}
                </div>

                {/* Info */}
                <div className="flex-grow min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="font-medium text-slate-900 truncate">
                      {member.first_name} {member.last_name}
                    </p>
                    {member.is_shift_lead && (
                      <Badge variant="outline" className="text-xs border-cyan-300 text-cyan-700">
                        <Shield className="w-3 h-3 mr-1" />
                        Lead
                      </Badge>
                    )}
                  </div>
                  <div className="flex items-center gap-3 text-xs text-slate-500 mt-0.5">
                    {member.checkin_time && (
                      <span className="text-green-600">In: {formatDateTime12h(member.checkin_time)}</span>
                    )}
                    {member.checkout_time && (
                      <span className="text-blue-600">Out: {formatDateTime12h(member.checkout_time)}</span>
                    )}
                    {member.checkin_method && member.checkin_method !== 'self' && (
                      <span className="text-amber-600">({member.checkin_method})</span>
                    )}
                  </div>
                </div>

                {/* Status Badge */}
                <div className="flex-shrink-0">
                  {getStatusBadge(member.attendance_status)}
                </div>

                {/* Quick Actions */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button 
                      variant="ghost" 
                      size="icon" 
                      disabled={actionLoading === member.booking_id}
                      data-testid={`action-menu-${member.booking_id}`}
                    >
                      {actionLoading === member.booking_id ? (
                        <RefreshCw className="w-4 h-4 animate-spin" />
                      ) : (
                        <MoreVertical className="w-4 h-4" />
                      )}
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    {member.attendance_status === 'pending' && (
                      <>
                        <DropdownMenuItem 
                          onClick={() => handleAction(member.booking_id, 'check_in', `${member.first_name} ${member.last_name}`)}
                          className="text-green-600"
                        >
                          <LogIn className="w-4 h-4 mr-2" />
                          Check In
                        </DropdownMenuItem>
                        <DropdownMenuItem 
                          onClick={() => handleAction(member.booking_id, 'late', `${member.first_name} ${member.last_name}`)}
                          className="text-amber-600"
                        >
                          <AlertTriangle className="w-4 h-4 mr-2" />
                          Mark Late
                        </DropdownMenuItem>
                        <DropdownMenuItem 
                          onClick={() => handleAction(member.booking_id, 'no_show', `${member.first_name} ${member.last_name}`)}
                          className="text-red-600"
                        >
                          <XCircle className="w-4 h-4 mr-2" />
                          No Show
                        </DropdownMenuItem>
                      </>
                    )}
                    {member.attendance_status === 'checked_in' && (
                      <DropdownMenuItem 
                        onClick={() => handleAction(member.booking_id, 'check_out', `${member.first_name} ${member.last_name}`)}
                        className="text-blue-600"
                      >
                        <LogOut className="w-4 h-4 mr-2" />
                        Check Out
                      </DropdownMenuItem>
                    )}
                    {member.attendance_status === 'no_show' && (
                      <DropdownMenuItem 
                        onClick={() => handleAction(member.booking_id, 'check_in', `${member.first_name} ${member.last_name}`)}
                        className="text-green-600"
                      >
                        <LogIn className="w-4 h-4 mr-2" />
                        Override - Check In
                      </DropdownMenuItem>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            ))}

            {staff.length === 0 && (
              <div className="text-center py-8 text-slate-500">
                No staff assigned to this event yet.
              </div>
            )}
          </CardContent>
        </Card>

        {/* Help Info */}
        <Alert>
          <Shield className="w-4 h-4" />
          <AlertDescription>
            As Shift Lead, you can check in/out staff, mark late arrivals, and record no-shows using the action menu on each staff member.
          </AlertDescription>
        </Alert>
      </main>
    </div>
  );
};

export default ShiftLeadDashboard;
